import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { PopupService } from '../../shared/modal/popup.service';
import { HttpResponse } from '@angular/common/http';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { SearchComposite } from '../../shared/model/search-composite';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { AlertService } from '../../shared/alert/alert.service';
import { JhiEventManager } from '../../shared/core-utils';
import { parseFile } from '../../shared/util/file-utils';
import { InventoryService } from '../../shared/inventory/service/inventory.service';
import { TransactionService } from '../../shared/inventory/service/transaction.service';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-transaction-update-dialog',
    templateUrl: './transaction-import-dialog.component.html'
})
export class TransactionImportDialogComponent implements OnInit, OnDestroy {

    @ViewChild('fileUpload')
        fileUpload: ElementRef;

    fileName = '';
    importData = new Array<any>();
    importHeader = [];
    subscriptionsManager = new SubscriptionsManager();
    importFormats = [
        { name: 'Excel', extension: '.xls,.xlsx' }
    ];

    selectedFileType = this.importFormats.map((formatExtension) => formatExtension.extension).join(',');


    constructor(
        private alertService: AlertService,
        public activeModal: NgbActiveModal,
        private modalService: NgbModal,
        private transactionService: TransactionService,
        private eventManager: JhiEventManager,
        private translateService: TranslateService) {

    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
    }

    close() {
        this.activeModal.dismiss('cancel');
    }

    import() {
        if (!this.importData || this.importData.length <= 1) {
            this.alertService.error('inventoryManagerApp.transaction.update.validation.file.empty');
            return;
        }
        if (this.validate()) {
            const transactionItems = this.importData
                .slice(1) // skip header
                .filter((row) => row.some((column) => Boolean(column.trim()))) // remove empty rows
                .map((row) => {
                    return Object.assign({
                        transactionId: row[this.importHeader[0]],
                        amount: row[this.importHeader[1]],
                        availableBalance: row[this.importHeader[2]],
                        notes: row[this.importHeader[3]],
                        storageLocationAbbr: row[this.importHeader[4]],
                        unit: row[this.importHeader[5]]
                    });
                });
            this.subscriptionsManager.add = this.transactionService.updateTransactions(transactionItems).subscribe(
                (res: HttpResponse<any>) => this.onSaveSuccess(transactionItems.map((i) => i.transactionId)));
        }
    }

    clearSelectedFile(): void {
        this.importData = null;
    }

    onFileChange(evt: any) {
        const target = evt.target;
        this.fileName = target.files[0].name;

        this.subscriptionsManager.add = parseFile(target.files[0], 'Transactions').subscribe((value) => {
            this.importData = value;
        });
    }

    private validate() {
        const errorMessage: string[] = [];
        this.validateHeader(this.importData[0], errorMessage);
        this.validateData(this.importData.slice(1), errorMessage);

        if (errorMessage.length !== 0) {
            this.alertService.error('error.custom', { param: formatErrorList(errorMessage) });
            return false;
        }
        return true;
    }

    private validateHeader(fileHeaders: string[], errorMessage: string[]) {
        this.importHeader = [];
        const headers = ['TRN_ID', 'New Amount', 'New Balance', 'New Notes', 'New Storage Location Abbr', 'New Unit'];
        headers.forEach((header) => {
            const result = fileHeaders.filter((fileHeader) =>
                fileHeader.toLowerCase() === header.toLowerCase()
            );

            if (result.length === 0) {
                errorMessage.push(this.translateService.instant('error.import.header.mandatory', { param: headers.join(', ') }));
            }

            if (result.length > 1) {
                errorMessage.push(this.translateService.instant('error.import.header.duplicated', { param: header }));
            }

            const idx = fileHeaders.indexOf(result[0]);
            this.importHeader.push(idx);
        });
    }

    private validateData(importData: any[], errorMessage: string[]) {
        for (let i = 0; i < importData.length; i++) {
            const row = importData[i];
            // TransactionId
            if (row[this.importHeader[0]] && (isNaN(row[this.importHeader[0]]) || row[this.importHeader[0]] < 0)) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.transaction.update.validation.transactionId'));
                break;
            }
            // New Amount
            if (row[this.importHeader[1]] && (isNaN(row[this.importHeader[1]]) || row[this.importHeader[1]] < 0)) {
                errorMessage.push(this.translateService.instant('error.import.amount.format'));
                break;
            }
            // New Balance
            if (row[this.importHeader[2]] && (isNaN(row[this.importHeader[2]]) || row[this.importHeader[2]] < 0)) {
                errorMessage.push(this.translateService.instant('error.import.amount.format'));
                break;
            }
        }
    }

    private onSaveSuccess(transactionIds) {
        const searchComposite: SearchComposite<any, any> = new SearchComposite();
        searchComposite.itemIds = transactionIds;

        this.alertService.success('inventoryManagerApp.transaction.update.success');
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: { filterBy: { transactionIds } } });

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('inventoryManagerApp.transaction.update.confirm');
        confirmModalRef.componentInstance.title = this.translateService.instant('inventoryManagerApp.transaction.update.confirm.title');
        confirmModalRef.result.then(() => {
            this.subscriptionsManager.add = this.transactionService.confirmPendingTransaction(searchComposite).subscribe(
                (res: HttpResponse<any>) => {
                    this.alertService.success('inventoryManagerApp.transaction.confirm.pending.success');
                    this.eventManager.broadcast({ name: 'columnFiltersChanged' });
                }
            );
            // TODO es2018 finally()
            this.activeModal.close();
        }, () => this.activeModal.close());
    }

}

@Component({
    selector: 'jhi-transaction-update-popup',
    template: ''
})
export class TransactionUpdatePopupComponent implements OnInit, OnDestroy {

    routeSub: any;

    constructor(private inventoryService: InventoryService,
                private route: ActivatedRoute,
                private popupService: PopupService
    ) {
    }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(() => {
            this.popupService.open(TransactionImportDialogComponent as Component);
        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }

}
